import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';
import { environment } from '@env/environment';
import { LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { AuthService } from 'app/auth/auth.service';
import { ComplaintDialogService } from 'app/services/complaints/complaint-dialog.service';
import { SharedService } from 'app/services/core/shared.service';
import { filter } from 'rxjs/operators';

@Component({
selector: 'app-home',
templateUrl: './home.component.html',
styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {
public environment = environment;
public uiData: any = LEAD_BASED_PAINT;
title: string = "Oregon Healthy Environments Client Portal";
titleMobile: string = "OR Healthy Env. Client Portal";
agreedToTerms: boolean = false;
showAttestationModal: boolean = false;
selectedOption: HomeOption | null = null;
private pendingAction: (() => void) | null = null;

homeOptions: HomeOption[] = [
	{
		key: 'training-providers',
		title: 'Training Providers',
		icon: 'school',
		description: [
			"Apply here if your organization needs to start or manage <strong>Accredited Training Provider</strong> activity with Oregon's lead-based paint program. This section supports both <strong>first-time applications</strong> and requests to <strong>link your account</strong> to an existing provider record.",
			'If you are creating a new Training Provider application, you can complete the workflow in stages, save your progress, and return later. The application collects key provider details and requires structured course and facility information, along with required supporting documents and final acknowledgments before submission.',
			'If your organization already exists in the system, use the link-request path to request account access. You will be asked to confirm the provider information and attest that you are affiliated before you submit. Link requests are reviewed before access is granted, and email notification is expected after review.',
			'After affiliation is established, ATP users can monitor key activity from their dashboard, including affiliated provider records, certificates needing attention, and open applications.',
		].join('<br><br>')
	},
	{
		key: 'firms',
		title: 'Firms',
		icon: 'business',
		description: [
			"Apply here if your organization needs to start or manage <strong>Firm</strong> activity with Oregon's lead-based paint program. This section supports both <strong>first-time applications</strong> and requests to <strong>link your account</strong> to an existing firm record.",
			'If you are creating a new Firm application, you can complete the workflow in stages, save your progress, and return later. The application collects key firm details and requires structured course and facility information, along with required supporting documents and final acknowledgments before submission.',
			'If your organization already exists in the system, use the link-request path to request account access. You will be asked to confirm the firm information and attest that you are affiliated before you submit. Link requests are reviewed before access is granted, and email notification is expected after review.',
			'After affiliation is established, Firm users can monitor key activity from their dashboard, including affiliated firm records, certificates needing attention, and open applications.',
		].join('<br><br>')
	},
	{
		key: 'individual',
		title: 'Individual',
		icon: 'person',
		description: [
			"Apply here if you need to start or manage <strong>Individual</strong> activity with Oregon's lead-based paint program. This section supports both <strong>first-time applications</strong> and requests to <strong>link your account</strong> to an existing individual record.",
			'If you are creating a new Individual application, you can complete the workflow in stages, save your progress, and return later. The application collects key individual details and requires structured course and facility information, along with required supporting documents and final acknowledgments before submission.',
			'If your organization already exists in the system, use the link-request path to request account access. You will be asked to confirm the individual information and attest that you are affiliated before you submit. Link requests are reviewed before access is granted, and email notification is expected after review.',
			'After affiliation is established, Individual users can monitor key activity from their dashboard, including affiliated individual records, certificates needing attention, and open applications.',
		].join('<br><br>')
	},
	{
		key: 'complaint',
		title: 'Submit Complaint',
		icon: 'comment',
		description: [
			'Use this form to report a lead-based paint concern to the Oregon Lead-Based Paint Program. You do not need to sign in to submit a complaint.',
			'The <strong>submission is anonymous</strong> but you may provide your <strong>name and contact information</strong> if you want potential follow-up from program staff, or you may leave submitter fields blank. The form asks for <strong>location details</strong> and a <strong>clear description</strong> of what happened so staff can review the report. You can also provide names or identifying details of people involved when known.',
			'After you submit, the system confirms receipt and provides a complaint reference number. Keep that number for your records. If you include contact information, staff may reach out if more information is needed during review.',
			'Please note that complaint information may be subject to public disclosure under Oregon Public Records Law. Provide clear, accurate details to support program review and any needed follow-up.',
		].join('<br><br>')
	}
];

constructor(
public sharedService: SharedService,
private router: Router,
public authService: AuthService,
private msalBroadcastService: MsalBroadcastService,
private readonly complaintDialogService: ComplaintDialogService,
) {}

public get authorized(): boolean{
return this.authService.isAuthorized();
}

ngOnInit(): void {
this.msalBroadcastService.msalSubject$
.pipe(
 filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS)
)
.subscribe(() => {
 this.router.navigateByUrl('/dashboard');
});
}

login(): void {
this.authService.login();
}

continue(): void {
this.requestAttestation(() => this.enterPortal());
}

openInfoPanel(option: HomeOption): void {
this.selectedOption = option;
}

closeInfoPanel(): void {
this.selectedOption = null;
}

startSelectedFlow(): void {
if (!this.selectedOption) {
return;
}

if (this.selectedOption.key === 'complaint') {
this.closeInfoPanel();
this.openComplaintDialog();
return;
}

this.closeInfoPanel();
this.requestAttestation(() => this.enterPortal());
}

closeAttestationModal(): void {
this.showAttestationModal = false;
this.agreedToTerms = false;
this.pendingAction = null;
}

confirmAttestation(): void {
if (!this.agreedToTerms) {
return;
}

this.showAttestationModal = false;
const action = this.pendingAction;
this.pendingAction = null;
this.agreedToTerms = false;
action?.();
}

enterPortal(): void {
if (this.authorized) {
this.router.navigateByUrl('/dashboard');
return;
}

this.login();
}

openComplaintDialog(): void {
this.complaintDialogService.open();
}

getOptionColor(option: HomeOption): string {
if (option.key === 'training-providers') {
return this.uiData.atp.color;
}

if (option.key === 'firms') {
return this.uiData.firm.color;
}

if (option.key === 'individual') {
return this.uiData.individual.color;
}

return this.uiData.complaint.color;
}

getOptionTint(option: HomeOption): string {
const color = this.getOptionColor(option);
return this.withAlpha(color, 0.72);
}

getOptionShadow(option: HomeOption): string {
const color = this.getOptionColor(option);
return '-1px -1px 0 1px ' + this.withAlpha(color, 0.98)
	+ ', -4px -4px 6px ' + this.withAlpha(color, 0.68)
	+ ', -7px -7px 9px ' + this.withAlpha(color, 0.46)
	+ ', 6px 8px 8px rgba(0, 0, 0, 0.58)'
	+ ', 10px 13px 14px rgba(0, 0, 0, 0.48)';
}

private requestAttestation(action: () => void): void {
this.pendingAction = action;
this.agreedToTerms = false;
this.showAttestationModal = true;
}

private withAlpha(hexColor: string, alpha: number): string {
const match = /^#([0-9a-f]{6})$/i.exec(hexColor || '');
if (!match) {
return hexColor;
}

const safeAlpha = Math.max(0, Math.min(1, alpha));
const alphaHex = Math.round(safeAlpha * 255).toString(16).padStart(2, '0');
return '#' + match[1] + alphaHex;
}


}

interface HomeOption {
key: 'training-providers' | 'firms' | 'individual' | 'complaint';
title: string;
icon: string;
description: string;
}
